import { z } from 'zod';
import { balanceAmountStringSchema } from '../currency';
import { ITEM_CATEGORIES, itemDefinitionIdSchema } from '../inventory/definitions';
import { marketCategorySchema } from '../inventory/market-categories';
import { raritySchema } from '../rarity';
import { MARKET_FEE_NON_VIP_PERCENT, MARKET_FEE_VIP_PERCENT } from '../trade/marketplace';
import { LISTING_STATES, MARKET_PAGE_SIZE_MAX, MARKET_SORTS } from './listing';

/**
 * The item marketplace's wire contract.
 *
 * ## What a request may never say
 *
 * No `accountId`, anywhere. The seller is the authenticated session, and the
 * buyer is too; a listing's seller is a property of the row. Every request
 * schema is `.strict()`, so an unknown field is a rejected request rather than
 * a silently ignored one.
 *
 * No fee, either. The percentage is resolved from the SELLER's VIP state at the
 * instant of settlement, inside the purchase transaction - a client that stated
 * one would be stating something it cannot know.
 *
 * ## What a response never carries
 *
 * A seller's e-mail, their balance, their other items, their VIP expiry, or any
 * internal id beyond the ones a client must send back. The public display name
 * is the only thing one player learns about another.
 */

export const MARKET_LISTINGS_ROUTE = '/api/market/listings' as const;
export const MARKET_MINE_ROUTE = '/api/market/listings/mine' as const;
export const MARKET_HISTORY_ROUTE = '/api/market/history' as const;
export const MARKET_QUOTE_ROUTE = '/api/market/quote' as const;
/** `:listingId` routes, as templates. The client substitutes the id. */
export const MARKET_LISTING_DETAIL_ROUTE = '/api/market/listings/:listingId' as const;
export const MARKET_LISTING_CANCEL_ROUTE = '/api/market/listings/:listingId/cancel' as const;
export const MARKET_LISTING_BUY_ROUTE = '/api/market/listings/:listingId/buy' as const;

export const marketListingRoute = (listingId: string, suffix = '') =>
  `/api/market/listings/${listingId}${suffix}`;

export const MARKET_CONTRACT_VERSION = 1;

const idempotencyKeySchema = z.string().min(8).max(128);
const currencySchema = z.enum(['gold', 'cash']);

export const listingStateSchema = z.enum(LISTING_STATES);
export const marketSortSchema = z.enum(MARKET_SORTS);

/* -------------------------------------------------------------------------- */
/*  Requests                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Browse filters. Every one is optional; together they must stay indexable.
 *
 * `name` searches the definition id, which is what stands in for a display name
 * until real names exist. It is a prefix/substring match on a bounded string,
 * never a regular expression a client could make expensive.
 */
/** Cursor, page size and the drawer the player has open. */
export const marketPageQuerySchema = z
  .object({
    cursor: z.string().min(1).max(200).optional(),
    limit: z.coerce.number().int().min(1).max(MARKET_PAGE_SIZE_MAX).optional(),
    /**
     * Filtered on the SERVER, like the browse.
     *
     * A client that fetched a page and then hid rows would be paginating one
     * list and displaying another: "carregar mais" would walk past items the
     * player asked to see, and a page could come back empty while more
     * matches existed.
     */
    category: marketCategorySchema.optional(),
  })
  .strict();

export const marketBrowseQuerySchema = z
  .object({
    cursor: z.string().min(1).max(200).optional(),
    limit: z.coerce.number().int().min(1).max(MARKET_PAGE_SIZE_MAX).optional(),
    sort: marketSortSchema.optional(),
    /**
     * The player's drawer, not the engine's `ItemCategory`.
     *
     * The two differ in one place and it is the place that matters to a
     * buyer: `consumable` covers both a pet food and a Tech Paint Bucket, and
     * nobody browsing for one wants the other.
     */
    category: marketCategorySchema.optional(),
    rarity: raritySchema.optional(),
    currency: currencySchema.optional(),
    minPrice: balanceAmountStringSchema.optional(),
    maxPrice: balanceAmountStringSchema.optional(),
    name: z.string().min(1).max(64).optional(),
  })
  .strict();

/**
 * One listing. The item is named by what the SELLER owns, never by its price:
 * an instance line carries an instance id and a stack line a definition and a
 * quantity, exactly as the direct trade's offer does.
 */
export const createListingRequestSchema = z.union([
  z
    .object({
      kind: z.literal('instance'),
      instanceId: z.string().uuid(),
      currency: currencySchema,
      price: balanceAmountStringSchema,
      idempotencyKey: idempotencyKeySchema,
    })
    .strict(),
  z
    .object({
      kind: z.literal('stack'),
      definitionId: itemDefinitionIdSchema,
      quantity: balanceAmountStringSchema,
      currency: currencySchema,
      price: balanceAmountStringSchema,
      idempotencyKey: idempotencyKeySchema,
    })
    .strict(),
]);

export const cancelListingRequestSchema = z
  .object({ idempotencyKey: idempotencyKeySchema })
  .strict();

/**
 * A purchase. It states the price the buyer is looking at.
 *
 * Not because the server trusts it - the listing's own price is what is
 * charged - but so a listing that changed underneath the buyer is REFUSED
 * rather than silently charged at a number they never saw. A listing's price
 * cannot be edited, so in practice this only ever catches a stale screen.
 */
export const buyListingRequestSchema = z
  .object({
    expectedPrice: balanceAmountStringSchema,
    expectedCurrency: currencySchema,
    idempotencyKey: idempotencyKeySchema,
  })
  .strict();

/** What a seller would receive at today's rate, before committing to a price. */
export const marketQuoteQuerySchema = z
  .object({
    price: balanceAmountStringSchema,
    currency: currencySchema,
  })
  .strict();

/* -------------------------------------------------------------------------- */
/*  Responses                                                                 */
/* -------------------------------------------------------------------------- */

export const marketItemWireSchema = z.object({
  kind: z.enum(['instance', 'stack']),
  /**
   * What a player reads. Resolved on the SERVER from the one central name
   * source, so a card can never print a raw id - and so the marketplace, the
   * trade, the inventory and a future drop toast all say the same thing.
   */
  name: z.string().min(1),
  /** 'Nave', 'Artefato', 'Pet'... A short line under the name. */
  categoryLabel: z.string(),
  /** 'MK II' for a pet, 'Nível 3' for everything else. */
  levelLabel: z.string().min(1),
  /** Still sent: the client resolves the ART from it, never a name. */
  definitionId: itemDefinitionIdSchema,
  category: z.enum(ITEM_CATEGORIES),
  rarity: raritySchema,
  level: z.number().int().min(1),
  quantity: balanceAmountStringSchema,
  /** Asset id in the generated manifest; null while the art does not exist. */
  assetKey: z.string().min(1).nullable(),
  /**
   * Corações Primordiais incorporados na nave anunciada (gate V28F.A).
   *
   * `null` em dois casos, e os dois são deliberados:
   *
   * - **não é uma nave.** Um artefacto não tem corações, e dizer `'0'` afirmaria
   *   uma propriedade que a categoria não tem;
   * - **o anúncio TERMINOU.** A contagem é uma propriedade viva da nave, e a
   *   nave já é de outra pessoa: publicá-la no histórico do vendedor contaria o
   *   que o COMPRADOR fez depois da venda. Um histórico diz o que acabou, não o
   *   que o item é agora.
   *
   * Uma nave num anúncio activo sem corações traz `'0'`, que é um facto.
   *
   * ## Lido ao vivo, e NÃO desnormalizado em `market_listings`
   *
   * `category`, `rarity` e `level` estão na linha do anúncio para que uma
   * navegação filtre e ordene sem junções. A contagem não: é lida de
   * `ship_instances`, por `instance_id`, no instante em que o anúncio é servido.
   * Uma segunda verdade que pode envelhecer é pior do que uma junção.
   *
   * E não pode divergir: incorporar é RECUSADO enquanto a nave tiver reserva
   * activa - que é exactamente o estado de "anunciada". Sem isso, um vendedor
   * poderia esvaziar o valor do anúncio depois de alguém o ter visto.
   *
   * String decimal, nunca um `number` de JSON: a mesma regra das moedas.
   */
  primordialHeartCount: balanceAmountStringSchema.nullable(),
});

export const marketListingWireSchema = z.object({
  listingId: z.string().uuid(),
  state: listingStateSchema,
  item: marketItemWireSchema,
  currency: currencySchema,
  price: balanceAmountStringSchema,
  /** PUBLIC display name only. An e-mail never crosses this boundary. */
  sellerName: z.string(),
  /** True when the caller is the seller: they may cancel, and may not buy. */
  isMine: z.boolean(),
  /** ISO, from the server's clock. Null once terminal. */
  expiresAt: z.string().nullable(),
  createdAt: z.string(),
});
export type MarketListingWire = z.infer<typeof marketListingWireSchema>;

export const marketBrowseResponseSchema = z.object({
  contractVersion: z.literal(MARKET_CONTRACT_VERSION),
  listings: z.array(marketListingWireSchema),
  nextCursor: z.string().nullable(),
});
export type MarketBrowseResponse = z.infer<typeof marketBrowseResponseSchema>;

export const marketListingResponseSchema = z.object({ listing: marketListingWireSchema });
export type MarketListingResponse = z.infer<typeof marketListingResponseSchema>;

/**
 * What a sale would cost the seller, at the rate that applies RIGHT NOW.
 *
 * An estimate, and labelled as one. The rate is resolved again at settlement,
 * so a subscription that lapses between listing and sale really does change
 * what the sale costs - which is why the interface has to say so.
 */
export const marketQuoteWireSchema = z.object({
  currency: currencySchema,
  price: balanceAmountStringSchema,
  feeTier: z.enum(['vip', 'standard']),
  feePercent: z.union([z.literal(MARKET_FEE_VIP_PERCENT), z.literal(MARKET_FEE_NON_VIP_PERCENT)]),
  /** Estimated, because the carry moves and the tier is re-resolved on sale. */
  estimatedFee: balanceAmountStringSchema,
  estimatedNet: balanceAmountStringSchema,
  /** True while the seller's VIP is running. Purely for the warning copy. */
  vipActive: z.boolean(),
});
export type MarketQuoteWire = z.infer<typeof marketQuoteWireSchema>;

export const marketQuoteResponseSchema = z.object({ quote: marketQuoteWireSchema });
export type MarketQuoteResponse = z.infer<typeof marketQuoteResponseSchema>;

/** One completed sale or purchase, as the caller's own history shows it. */
export const marketHistoryEntryWireSchema = z.object({
  listingId: z.string().uuid(),
  /** Which side the caller was on. */
  role: z.enum(['seller', 'buyer']),
  /**
   * How the listing ended.
   *
   * A history that showed only sales would leave a seller wondering where a
   * cancelled listing went, so cancellations and expiries are in it too - and
   * they carry no fee, because nothing was charged.
   */
  outcome: z.enum(['sold', 'cancelled', 'expired']),
  item: marketItemWireSchema,
  currency: currencySchema,
  price: balanceAmountStringSchema,
  /** Present for the seller only: what the fee took and what was received. */
  fee: balanceAmountStringSchema.nullable(),
  net: balanceAmountStringSchema.nullable(),
  feeTier: z.enum(['vip', 'standard']).nullable(),
  /** Empty for a cancellation or an expiry: there was no counterparty. */
  counterpartyName: z.string(),
  /** When it ended, whatever ended it. */
  endedAt: z.string(),
});

export const marketHistoryResponseSchema = z.object({
  contractVersion: z.literal(MARKET_CONTRACT_VERSION),
  entries: z.array(marketHistoryEntryWireSchema),
  nextCursor: z.string().nullable(),
});
export type MarketHistoryResponse = z.infer<typeof marketHistoryResponseSchema>;

/**
 * The warning the sell tab must show, verbatim.
 *
 * Kept here rather than in a component so the rule and its wording travel
 * together: the fee is resolved at SETTLEMENT, and a seller has to be told that
 * before they price anything.
 */
export const MARKET_FEE_TIMING_NOTICE =
  'A taxa é calculada quando o item for vendido. Se o VIP expirar antes da venda, a taxa será de 23%.';
